/**
 * ContainerJoinPage — sender requests to join a container with their
 * shipment estimate + destination + supplier info.
 *
 * Required server fields (Joi `joinRequestSchema`):
 *   category, estWeightKg, declaredValueUsd,
 *   destinationCityName, destinationAddressText,
 *   destinationContactPhone, destinationContactName.
 *
 * Optional but valuable: estCbm, hsCode, supplier{Name,Phone,InvoiceUrl},
 * piecesCount, deliveryMode, insuranceOptedIn.
 *
 * Mirrors Flutter `containers/container_join_page.dart`.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Container, Loader, AlertCircle, Globe, ArrowLeftRight,
  ShieldCheck, Truck,
} from 'lucide-react';
import { useContainer, useContainerQuote, useJoinContainer } from '@/hooks/useContainers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import { containerPriceLabel } from '@/utils/containerStatus';
import type { JoinContainerInput, DeliveryMode } from '@/api/containers';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

// Common categories (lowercase) — must match server's category vocabulary.
// Lighter list than the full ALLOWED_CATEGORIES; covers 90% of senders.
const CATEGORY_OPTIONS = [
  { value: 'cosmetics',         label: 'مستحضرات تجميل' },
  { value: 'electronics',       label: 'إلكترونيات' },
  { value: 'fashion',           label: 'أزياء + أحذية' },
  { value: 'home_furniture',    label: 'منزلية + أثاث' },
  { value: 'auto_parts',        label: 'قطع سيارات' },
  { value: 'toys',              label: 'ألعاب' },
  { value: 'sports',            label: 'رياضية' },
  { value: 'machinery',         label: 'آلات ومعدات' },
  { value: 'tools',             label: 'أدوات' },
  { value: 'stationery',        label: 'قرطاسية' },
  { value: 'medical_devices',   label: 'أجهزة طبية' },
];

export default function ContainerJoinPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const containerId = id ? Number(id) : null;
  const { data: container, isLoading } = useContainer(containerId);
  const join = useJoinContainer();
  const quote = useContainerQuote();

  const [form, setForm] = useState<JoinContainerInput>({
    category: '',
    estWeightKg: 0,
    declaredValueUsd: 0,
    destinationCityName: '',
    destinationAddressText: '',
    destinationContactPhone: '',
    destinationContactName: '',
    deliveryMode: 'door',
    insuranceOptedIn: true,
    piecesCount: 1,
  });
  const [error, setError] = useState<string | null>(null);

  // Live quote — debounced 400ms when key fields change. The server's
  // quote response gives a precise breakdown so the client can show it
  // before commit.
  useEffect(() => {
    if (!containerId || !form.estWeightKg || !form.declaredValueUsd) return;
    const handle = setTimeout(() => {
      quote.mutate({
        containerId,
        estWeightKg: form.estWeightKg,
        estCbm: form.estCbm,
        declaredValueUsd: form.declaredValueUsd,
        insuranceOptedIn: form.insuranceOptedIn,
        deliveryMode: form.deliveryMode,
      });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, form.estWeightKg, form.estCbm, form.declaredValueUsd, form.insuranceOptedIn, form.deliveryMode]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!container) {
    return (
      <AppLayout>
        <p className="text-center text-text-secondary py-16">
          {t('container_not_found', 'الحاوية غير موجودة.')}
        </p>
      </AppLayout>
    );
  }

  // Server requires phone format /^\+?[1-9]\d{7,14}$/ (8-15 digits +/- prefix)
  const validPhone = (s: string) => /^\+?[1-9]\d{7,14}$/.test(s.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.category) return setError(t('error_category_required', 'فئة البضاعة إلزامية'));
    if (!form.estWeightKg || form.estWeightKg <= 0) return setError(t('error_weight_required', 'الوزن إلزامي'));
    if (!form.declaredValueUsd || form.declaredValueUsd <= 0) return setError(t('error_value_required', 'القيمة المُصرَّحة إلزامية'));
    if (form.destinationCityName.trim().length < 2) return setError(t('error_city_required', 'مدينة الوجهة إلزامية'));
    if (form.destinationAddressText.trim().length < 3) return setError(t('error_address_required', 'العنوان التفصيلي إلزامي (3 أحرف على الأقل)'));
    if (!validPhone(form.destinationContactPhone)) return setError(t('error_phone_invalid', 'رقم هاتف المستلم غير صالح (8-15 رقم، يبدأ بـ + اختياري)'));
    if (form.destinationContactName.trim().length < 2) return setError(t('error_receiver_name_required', 'اسم المستلم إلزامي (حرفان على الأقل)'));

    try {
      const p = await join.mutateAsync({
        containerId: containerId!,
        ...form,
        category: form.category.toLowerCase(),
      });
      navigate(`/containers/participant/${p.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.message || t('error_join_failed', 'فشل الانضمام'));
    }
  };

  // Validate min/max declared value against container restrictions
  const valueWarning =
    container.min_declared_value_usd != null && form.declaredValueUsd > 0 && form.declaredValueUsd < container.min_declared_value_usd
      ? t('value_too_low_warn', `الحد الأدنى للقيمة في هذه الحاوية ${container.min_declared_value_usd}$`)
      : container.max_declared_value_usd != null && form.declaredValueUsd > container.max_declared_value_usd
      ? t('value_too_high_warn', `الحد الأعلى للقيمة في هذه الحاوية ${container.max_declared_value_usd}$`)
      : null;

  const weightWarning =
    container.max_piece_weight_kg != null && form.estWeightKg > 0 && form.estWeightKg / (form.piecesCount || 1) > container.max_piece_weight_kg
      ? t('piece_weight_warn', `الحد الأعلى لوزن القطعة الواحدة في هذه الحاوية ${container.max_piece_weight_kg} kg`)
      : null;

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-2xl mx-auto space-y-4">
        {/* Container summary */}
        <div className="bg-surface border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Container size={18} className="text-primary" />
            <span className="font-bold text-text-primary" dir="ltr">{container.public_code}</span>
          </div>
          <div className="text-sm text-text-secondary mb-1 truncate">{container.title}</div>
          <div className="flex items-center gap-1 text-sm text-text-secondary">
            <Globe size={12} />
            <span dir="ltr">{container.origin_country?.toUpperCase()}</span>
            <ArrowLeftRight size={12} />
            <span dir="ltr">{container.destination_country?.toUpperCase()}</span>
          </div>
          {containerPriceLabel(container) && (
            <div className="text-xs text-text-tertiary mt-2">
              {t('rate', 'السعر')}: <strong className="text-primary">{containerPriceLabel(container)}</strong>
            </div>
          )}
          {container.requires_invoice && (
            <div className="text-xs text-warning mt-2 flex items-center gap-1">
              <AlertCircle size={11} /> {t('requires_invoice', 'فاتورة المورّد إلزامية لهذه الحاوية')}
            </div>
          )}
          {container.requires_hs_code && (
            <div className="text-xs text-warning mt-1 flex items-center gap-1">
              <AlertCircle size={11} /> {t('requires_hs_code', 'كود HS إلزامي لهذه الحاوية')}
            </div>
          )}
        </div>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={submit} className="bg-surface border border-border rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-bold text-text-secondary">
            {t('your_shipment', 'تفاصيل بضاعتك')}
          </h2>

          {/* Category */}
          <Field label={t('category_label', 'فئة البضاعة') + ' *'}>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className={FIELD_CLASSES}
              required
            >
              <option value="">{t('select_category', 'اختر فئة...')}</option>
              {CATEGORY_OPTIONS.map((c) => {
                const allowed = !container.allowed_categories || container.allowed_categories.includes(c.value);
                const prohibited = container.prohibited_categories?.includes(c.value);
                if (!allowed || prohibited) return null;
                return <option key={c.value} value={c.value}>{c.label}</option>;
              })}
            </select>
          </Field>

          {/* Weight + CBM + pieces */}
          <div className="grid grid-cols-3 gap-3">
            <TextField
              label={t('weight_kg', 'الوزن (kg)') + ' *'}
              type="number"
              value={form.estWeightKg || ''}
              onChange={(e) => setForm({ ...form, estWeightKg: Number(e.target.value) })}
              min={1} step={0.1} required dir="ltr"
            />
            <TextField
              label={t('cbm_optional', 'الحجم m³')}
              type="number"
              value={form.estCbm ?? ''}
              onChange={(e) => setForm({ ...form, estCbm: e.target.value === '' ? undefined : Number(e.target.value) })}
              min={0} step={0.001} dir="ltr"
            />
            <TextField
              label={t('pieces_count', 'عدد القطع')}
              type="number"
              value={form.piecesCount ?? 1}
              onChange={(e) => setForm({ ...form, piecesCount: Number(e.target.value) || 1 })}
              min={1} dir="ltr"
            />
          </div>
          {weightWarning && (
            <div className="text-xs text-warning -mt-2">⚠ {weightWarning}</div>
          )}

          {/* Declared value */}
          <TextField
            label={t('declared_value_usd', 'القيمة المُصرَّحة (USD)') + ' *'}
            type="number"
            value={form.declaredValueUsd || ''}
            onChange={(e) => setForm({ ...form, declaredValueUsd: Number(e.target.value) })}
            min={1} required dir="ltr"
          />
          {valueWarning && (
            <div className="text-xs text-warning -mt-2">⚠ {valueWarning}</div>
          )}

          {/* HS code (when required) */}
          {container.requires_hs_code && (
            <TextField
              label={t('hs_code', 'كود HS')}
              value={form.hsCode || ''}
              onChange={(e) => setForm({ ...form, hsCode: e.target.value })}
              dir="ltr" placeholder="6109.10"
            />
          )}

          {/* Title */}
          <TextField
            label={t('title_optional', 'وصف مختصر')}
            value={form.title || ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t('title_ph', 'مثلاً: 5 صناديق ملابس نسائية')}
          />

          {/* Supplier (optional but encouraged) */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('supplier_info', 'بيانات المورّد')} {container.requires_invoice && '*'}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('supplier_name', 'اسم المورّد')}
                value={form.supplierName || ''}
                onChange={(e) => setForm({ ...form, supplierName: e.target.value })}
              />
              <TextField
                label={t('supplier_phone', 'هاتف المورّد')}
                value={form.supplierPhone || ''}
                onChange={(e) => setForm({ ...form, supplierPhone: e.target.value })}
                dir="ltr"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('supplier_invoice_number', 'رقم الفاتورة')}
                value={form.supplierInvoiceNumber || ''}
                onChange={(e) => setForm({ ...form, supplierInvoiceNumber: e.target.value })}
                dir="ltr"
              />
              <TextField
                label={t('supplier_invoice_url', 'رابط صورة الفاتورة')}
                type="url"
                value={form.supplierInvoiceUrl || ''}
                onChange={(e) => setForm({ ...form, supplierInvoiceUrl: e.target.value })}
                dir="ltr"
              />
            </div>
          </fieldset>

          {/* Destination */}
          <fieldset className="space-y-3 pt-2 border-t border-border/50">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('delivery_destination', 'وجهة التسليم')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('receiver_name', 'اسم المستلم') + ' *'}
                value={form.destinationContactName}
                onChange={(e) => setForm({ ...form, destinationContactName: e.target.value })}
                required
              />
              <TextField
                label={t('receiver_phone', 'هاتف المستلم') + ' *'}
                value={form.destinationContactPhone}
                onChange={(e) => setForm({ ...form, destinationContactPhone: e.target.value })}
                required dir="ltr"
                placeholder="+963999..."
              />
            </div>
            <TextField
              label={t('destination_city', 'مدينة الوجهة') + ' *'}
              value={form.destinationCityName}
              onChange={(e) => setForm({ ...form, destinationCityName: e.target.value })}
              required
              placeholder={t('city_ph', 'مثلاً: دمشق')}
            />
            <Field label={t('destination_address', 'العنوان التفصيلي') + ' *'}>
              <textarea
                value={form.destinationAddressText}
                onChange={(e) => setForm({ ...form, destinationAddressText: e.target.value })}
                className={FIELD_CLASSES + ' min-h-[60px]'}
                required
                placeholder={t('address_ph', 'الحي + الشارع + رقم البناء + علامة مميزة')}
              />
            </Field>
          </fieldset>

          {/* Toggles */}
          <fieldset className="pt-2 border-t border-border/50 space-y-2">
            <Toggle
              checked={form.insuranceOptedIn ?? true}
              onChange={(v) => setForm({ ...form, insuranceOptedIn: v })}
              icon={<ShieldCheck size={16} className="text-success" />}
              label={t('insurance_label', 'تأمين على البضاعة')}
              hint={t('insurance_hint', 'موصى به للقيم أكثر من $500')}
            />
            <Toggle
              checked={form.deliveryMode === 'door'}
              onChange={(v) => setForm({ ...form, deliveryMode: (v ? 'door' : 'pickup_at_warehouse') as DeliveryMode })}
              icon={<Truck size={16} className="text-primary" />}
              label={t('door_to_door_label', 'توصيل لباب البيت')}
              hint={t('door_to_door_hint', 'بدون: تستلمها من المستودع. مع: تصل لعنوانك.')}
            />
          </fieldset>

          {/* Live Quote */}
          {quote.data && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-2">
              <div className="text-xs text-text-secondary mb-1">{t('estimated_total', 'السعر التقديري الإجمالي')}</div>
              <div className="text-2xl font-bold text-primary">
                ${Number(quote.data.total_usd).toLocaleString()}
              </div>
              <div className="text-xs text-text-tertiary mt-2 space-y-0.5">
                <div>{t('shipping', 'الشحن')}: ${Number(quote.data.shipping_usd).toFixed(2)}</div>
                {quote.data.clearance_usd > 0 && (
                  <div>{t('clearance', 'التخليص')}: ${Number(quote.data.clearance_usd).toFixed(2)}</div>
                )}
                {quote.data.insurance_usd > 0 && (
                  <div>{t('insurance', 'التأمين')}: ${Number(quote.data.insurance_usd).toFixed(2)}</div>
                )}
                {quote.data.final_mile_usd > 0 && (
                  <div>{t('door_delivery', 'التوصيل للباب')}: ${Number(quote.data.final_mile_usd).toFixed(2)}</div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-3">
            <Button variant="primary" loading={join.isPending} type="submit">
              {t('submit_join', 'إرسال طلب الانضمام')}
            </Button>
            <Button variant="ghost" type="button" onClick={() => navigate(-1)}>
              {t('cancel', 'إلغاء')}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function Toggle({
  checked, onChange, icon, label, hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer p-2 rounded-xl hover:bg-border/20 transition">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-primary"
      />
      <span className="flex items-center gap-2">{icon}</span>
      <span className="flex-1">
        <span className="font-semibold text-sm text-text-primary block">{label}</span>
        {hint && <span className="text-xs text-text-tertiary">{hint}</span>}
      </span>
    </label>
  );
}
